import type { VirtualElement } from '@popperjs/core';
import { createPopper as createPopperCore } from '@popperjs/core';

/**
 * Popper measures an element by walking its ancestors up to
 *  `ownerDocument.body` and calling `getComputedStyle()` on what it finds. When
 *  the document has been torn down that walk ends on `null` instead of the body
 *  element, and `getComputedStyle(null)` throws:
 *
 *  > Failed to execute 'getComputedStyle' on 'Window': parameter 1 is not of
 *  > type 'Element'.
 *
 * Popper's own precondition — `areValidElements` — only asks whether the
 *  elements are `Element`s, which a node in a dead document still is.
 */
export function isPopperElementMeasurable(
  element: Element | VirtualElement | null | undefined
): boolean {
  if (!element) {
    return false;
  }

  // a virtual element reports its own rect. Popper only walks the DOM for one
  //  when it carries a context element.
  const node = 'nodeType' in element ? element : element.contextElement;
  if (!node) {
    return true;
  }

  return node.isConnected && Boolean(node.ownerDocument?.body);
}

/**
 * `createPopper`, except it skips a positioning pass while the elements it
 *  would measure are not in a document it can measure.
 *
 * Popper's update is deferred — it debounces into a microtask, and popper
 *  installs that same update as its own `scroll` and `resize` listener — so it
 *  routinely runs against DOM that has since gone away. The throw that follows
 *  becomes an unhandled promise rejection, because popper wraps the update in
 *  `new Promise(...)` and its listener drops the promise.
 *
 * Skipping loses nothing: there is no visible popover to place, and the next
 *  update measures again from scratch.
 */
export const createMeasurablePopper: typeof createPopperCore = (
  reference,
  popper,
  options
) => {
  const instance = createPopperCore(reference, popper, options);
  const { forceUpdate } = instance;

  instance.forceUpdate = () => {
    if (
      !isPopperElementMeasurable(reference) ||
      !isPopperElementMeasurable(popper)
    ) {
      return;
    }

    forceUpdate();
  };

  return instance;
};
